import "@/App.css";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { PopupWidget } from 'react-calendly';
import { Navbar }                    from "./components/Navbar";
import { HeroSection }               from "./components/HeroSection";
import { ServiceCategoriesSection }  from "./components/ServiceCategoriesSection";
import { ServiceFinderSection }      from "./components/ServiceFinderSection";
import { WhyUsSection }              from "./components/WhyUsSection";
import { IndustriesSection }         from "./components/IndustriesSection";
import { AboutSection }              from "./components/AboutSection";
import { CTASection }                from "./components/CTASection";
import { ContactSection }            from "./components/ContactSection";
import { Footer }                    from "./components/Footer";
import { SEO, ORGANIZATION_SCHEMA }  from "./components/SEO";
import { BookingPage }               from "./pages/BookingPage";
import { ShopifyServicesPage }       from "./pages/services/ShopifyServicesPage";
import { ITServicesPage }            from "./pages/services/ITServicesPage";
import { NetworkingServicesPage }    from "./pages/services/NetworkingServicesPage";
import { SoftwareServicesPage }      from "./pages/services/SoftwareServicesPage";
import { EmailWorkplacePage }        from "./pages/services/EmailWorkplacePage";
import { IntegrationsPage }          from "./pages/services/IntegrationsPage";
import { MarketingServicesPage }     from "./pages/services/MarketingServicesPage";
import { MapifyFeaturesPage }        from "./pages/apps/mapify/MapifyFeaturesPage";
import { MapifyTermsPage }           from "./pages/apps/mapify/MapifyTermsPage";
import { MapifyPrivacyPage }         from "./pages/apps/mapify/MapifyPrivacyPage";
import { TickedFeaturesPage }        from "./pages/apps/ticked/TickedFeaturesPage";
import { TickedTermsPage }           from "./pages/apps/ticked/TickedTermsPage";
import { TickedPrivacyPage }         from "./pages/apps/ticked/TickedPrivacyPage";
import { ProductlyFeaturesPage }     from "./pages/apps/productly/ProductlyFeaturesPage";
import { ProductlyTermsPage }        from "./pages/apps/productly/ProductlyTermsPage";
import { ProductlyPrivacyPage }      from "./pages/apps/productly/ProductlyPrivacyPage";
import { UpsellPlansProFeaturesPage } from "./pages/apps/upsell-plans-pro/UpsellPlansProFeaturesPage";
import { UpsellPlansProTermsPage }    from "./pages/apps/upsell-plans-pro/UpsellPlansProTermsPage";
import { UpsellPlansProPrivacyPage }  from "./pages/apps/upsell-plans-pro/UpsellPlansProPrivacyPage";

const HOME_FAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does Smart Solutions do?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Smart Solutions is a full-service technology partner offering Shopify development, custom software, IT services, networking infrastructure, email and workplace setup, and third-party integrations. We are certified Shopify Expert Partners building and scaling e-commerce stores end-to-end.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are you a certified Shopify Partner?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Smart Solutions is a certified Shopify Expert Partner with deep experience in Shopify Plus migrations, custom theme development, and private/public Shopify app development using Shopify Functions, UI Extensions, and the latest Admin and Storefront APIs.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I start a project with Smart Solutions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Book a free consultation through our booking page or email hello@smartsolution.ai. We start with a discovery call to understand your goals, then provide a scoped proposal with timelines and pricing before any work begins.',
      },
    },
    {
      '@type': 'Question',
      name: 'What industries do you work with?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We serve direct-to-consumer brands, B2B wholesalers, retail businesses, SaaS companies, and small-to-mid-size enterprises across e-commerce, professional services, hospitality, and technology sectors.',
      },
    },
  ],
};

const MainSite = () => (
  <div className="App" data-testid="app">
    <SEO
      title="Shopify Experts, Custom Software & IT Services"
      description="Smart Solutions is a certified Shopify Expert Partner agency. We build custom Shopify stores, apps, integrations, IT infrastructure, and software for growing businesses."
      path="/"
      jsonLd={[ORGANIZATION_SCHEMA, HOME_FAQ]}
    />
    <Navbar />
    <main>
      <HeroSection />
      <ServiceCategoriesSection />
      <ServiceFinderSection />
      <WhyUsSection />
      <IndustriesSection />
      <AboutSection />
      <CTASection />
      <ContactSection />
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainSite />} />
        <Route path="/book" element={<BookingPage />} />
        <Route path="/services/shopify" element={<ShopifyServicesPage />} />
        <Route path="/services/it-solutions" element={<ITServicesPage />} />
        <Route path="/services/networking" element={<NetworkingServicesPage />} />
        <Route path="/services/software" element={<SoftwareServicesPage />} />
        <Route path="/services/email-workplace" element={<EmailWorkplacePage />} />
        <Route path="/services/integrations" element={<IntegrationsPage />} />
        <Route path="/services/marketing" element={<MarketingServicesPage />} />
        <Route path="/apps/mapify" element={<MapifyFeaturesPage />} />
        <Route path="/apps/mapify/terms" element={<MapifyTermsPage />} />
        <Route path="/apps/mapify/privacy" element={<MapifyPrivacyPage />} />
        <Route path="/apps/ticked" element={<TickedFeaturesPage />} />
        <Route path="/apps/ticked/terms" element={<TickedTermsPage />} />
        <Route path="/apps/ticked/privacy" element={<TickedPrivacyPage />} />
        <Route path="/apps/productly" element={<ProductlyFeaturesPage />} />
        <Route path="/apps/productly/terms" element={<ProductlyTermsPage />} />
        <Route path="/apps/productly/privacy" element={<ProductlyPrivacyPage />} />
        <Route path="/apps/upsell-plans-pro" element={<UpsellPlansProFeaturesPage />} />
        <Route path="/apps/upsell-plans-pro/terms" element={<UpsellPlansProTermsPage />} />
        <Route path="/apps/upsell-plans-pro/privacy" element={<UpsellPlansProPrivacyPage />} />
      </Routes>
      <a
        href="https://wa.me/96565966786"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          backgroundColor: '#25D366',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          textDecoration: 'none',
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="white"
          width="28"
          height="28"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
      <PopupWidget
        url="https://calendly.com/hello-smartsolution/30min"
        rootElement={document.getElementById('root')}
        text="Schedule time with me"
        textColor="#ffffff"
        color="#0069ff"
      />
    </BrowserRouter>
  );
}

export default App;
